import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { Dropdown } from "components";
import printJS from "lib/print-js";
import { APPOINTMENT_NOTE_TYPES, KEYLABEL_LAYOUTS, KEYLABEL_TYPES } from "models";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";

import type { Appointment, DealerLocation } from "models";

interface KeyLabelPrintContentProps {
  printSize: KEYLABEL_LAYOUTS;
  appointment: Appointment;
  selectedLocation?: DealerLocation;
}

interface KeyLabelModalProps {
  handleCloseCarInShopPrintModal: () => void;
  carInShopPrintVisible: boolean;
  keyLabelPrintSize: KEYLABEL_LAYOUTS;
  handleChangeKeyLabelPrintSize: (data: KEYLABEL_LAYOUTS) => void;
  appointment: Appointment;
}

const KeyLabelPrintContent = ({ printSize, appointment, selectedLocation }: KeyLabelPrintContentProps) => {
  const customerDriverName = `${appointment.customer_driver?.firstname || ""} ${appointment.customer_driver?.surname || ""}`.trim();

  if (printSize === KEYLABEL_LAYOUTS.KeyTag) {
    return (
      <>
        <div className="car_in_shop_print_large">{appointment.reg_number}</div>
        {customerDriverName && <div>{customerDriverName}</div>}
      </>
    );
  }

  if (printSize === KEYLABEL_LAYOUTS.AltecQR) {
    const customerWaiting = appointment.customer_waiting ? "Wachter" : "";
    const carMakeModel = `${appointment.car_make || ""} ${appointment.car_model || ""}`.trim();

    const qrValue = `QTY$="1"
    IdNr$="${appointment.dms_nr || ""}"
    WONr$="${appointment.wo_nr || ""}"
    Wacht$="${customerWaiting}"
    Naam$="${customerDriverName}"
    Model$="${carMakeModel}"
    Kenteken$="${appointment.reg_number || ""}"
    RUN L1.BAS`;

    return (
      <div className="keylabel-qrcode-container">
        <QRCodeSVG value={qrValue} size={400} fgColor="#000000" />
      </div>
    );
  }

  const labelContent = (
    <>
      {selectedLocation?.key_label_type_id === KEYLABEL_TYPES.Standard && (
        <>
          <div>{appointment.car_make}</div>
          <div>{appointment.car_model}</div>
        </>
      )}
      <div className="car_in_shop_print_large parking_location_row">
        {appointment.reg_number} {appointment.notes?.find(note => note.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.ParkingLocation)?.note ?? ""}
      </div>
      <div className="car_in_shop_print_large">{appointment.vin_nr}</div>
      <div className="car_in_shop_print_large car_in_shop_print_bold">{appointment.wo_nr}</div>
      {customerDriverName && <div>{customerDriverName}</div>}
    </>
  );

  if (printSize === KEYLABEL_LAYOUTS.LabelWriter400) return <div className="label_writer_400_rotated">{labelContent}</div>;

  return labelContent;
};

const KeyLabelModal = ({ carInShopPrintVisible, handleCloseCarInShopPrintModal, keyLabelPrintSize, handleChangeKeyLabelPrintSize, appointment }: KeyLabelModalProps) => {
  const { t } = useTranslation();
  const selectedLocation = useAppointmentLocation();

  const labelTypeOptions = [
    { text: "A4", value: KEYLABEL_LAYOUTS.A4, key: KEYLABEL_LAYOUTS.A4 },
    { text: `${t("v8_label", "Label")} - 29 × 62 mm`, value: KEYLABEL_LAYOUTS.Label, key: KEYLABEL_LAYOUTS.Label },
    { text: `${t("v8_label", "Label")} - 29 × 90 mm`, value: KEYLABEL_LAYOUTS.LongLabel, key: KEYLABEL_LAYOUTS.LongLabel },
    { text: `${t("v8_kiosk_label", "Kiosk Label")} - 19.1 × 279.4 mm`, value: KEYLABEL_LAYOUTS.KioskLabel, key: KEYLABEL_LAYOUTS.KioskLabel },
    { text: `${t("v8_keytag", "Keytag")} - 35 × 108 mm`, value: KEYLABEL_LAYOUTS.KeyTag, key: KEYLABEL_LAYOUTS.KeyTag },
    { text: "LabelWriter 400 - 57 × 32 mm", value: KEYLABEL_LAYOUTS.LabelWriter400, key: KEYLABEL_LAYOUTS.LabelWriter400 },
    { text: "Altec QR", value: KEYLABEL_LAYOUTS.AltecQR, key: KEYLABEL_LAYOUTS.AltecQR }
  ];

  const getPrintableId = () => {
    switch (keyLabelPrintSize) {
      case KEYLABEL_LAYOUTS.KeyTag:
        return "car_in_shop_keytag_content";
      case KEYLABEL_LAYOUTS.A4:
        return "car_in_shop_a4_content";
      case KEYLABEL_LAYOUTS.Label:
        return "car_in_shop_label_content";
      case KEYLABEL_LAYOUTS.KioskLabel:
        return "car_in_shop_kiosk_label_content";
      case KEYLABEL_LAYOUTS.LongLabel:
        return "car_in_shop_long_label_content";
      case KEYLABEL_LAYOUTS.AltecQR:
        return "car_in_shop_altec_qr_content";
      case KEYLABEL_LAYOUTS.LabelWriter400:
        return "car_in_shop_label_writer_400_content";
      default:
        return "";
    }
  };

  const handleKeyLabelDropdownChange = (labelLayout?: number) => labelLayout && handleChangeKeyLabelPrintSize(labelLayout);

  const handlePrint = () => {
    printJS({
      printable: getPrintableId(),
      type: "html",
      copyStyles: true,
      scanStyles: keyLabelPrintSize !== KEYLABEL_LAYOUTS.LabelWriter400
    });
  };

  return (
    <Modal
      className={`IndicatorsActionModal ${keyLabelPrintSize === KEYLABEL_LAYOUTS.KioskLabel ? "KioskPrintModal" : ""}`}
      closeOnEscape
      dimmer="blurring"
      closeOnDimmerClick={false}
      open={carInShopPrintVisible}
      onClose={handleCloseCarInShopPrintModal}
    >
      <Modal.Actions>
        <Dropdown selection options={labelTypeOptions} value={keyLabelPrintSize} onChange={handleKeyLabelDropdownChange} />
        {keyLabelPrintSize !== KEYLABEL_LAYOUTS.AltecQR && (
          <Button color="grey" onClick={handlePrint}>
            <Icon className="print" />
            {t("v8_print", "Print")}
          </Button>
        )}

        <Button className="light" onClick={handleCloseCarInShopPrintModal}>
          <Icon className="xmark" size="big" />
        </Button>
      </Modal.Actions>
      <Modal.Content scrolling id={getPrintableId()}>
        <KeyLabelPrintContent printSize={keyLabelPrintSize} appointment={appointment} selectedLocation={selectedLocation} />
      </Modal.Content>
    </Modal>
  );
};

export default KeyLabelModal;
